import { useDispatch, useSelector } from 'react-redux';
import './ShippingForm.css';
import { 
    selectContactInformation, 
    selectRecipientInformation, 
    selectShippingInformation, 
    selectShippingMethod, 
    selectShippingOptions, 
    setContactEmail, 
    setContactPhone, 
    setRecipientCompany, 
    setRecipientFirstName, 
    setRecipientLastName,
    setShippingAddressLine1,
    setShippingAddressLine2,
    setShippingCity,
    setShippingMethod,
    setShippingState,
    setShippingZip
} from './shippingFormSlice';
import { addCartItem, selectCart, setCategories, setCollections, setProducts } from '../shopPage/shopPageSlice';
import { useCreateStripeCheckoutSessionMutation, useGetOrderInfoMutation, useGetPricesMutation, useGetProductsMutation } from '../../services/api';
import LoadingSpinner from '../../components/loadingSpinner/LoadingSpinner';
import { useSearchParams } from 'react-router';
import { useCallback, useEffect, useRef } from 'react';

export default function ShippingForm() {
    const dispatch = useDispatch();
    const [ searchParams ] = useSearchParams();
    const orderInfoFetchedRef = useRef(false);

    const session_id = searchParams.get('session_id');

    const contactInformation = useSelector(selectContactInformation);
    const recipientInformation = useSelector(selectRecipientInformation);
    const shippingInformation = useSelector(selectShippingInformation);
    const shippingOptions = useSelector(selectShippingOptions);
    const selectedShipMethod = useSelector(selectShippingMethod);

    const cart = useSelector(selectCart);
    const cartTotal = cart.reduce((acc, cur) => acc + (cur.price * cur.quantity), 0);
    const numOfItems = cart.reduce((acc, cur) => acc + cur.quantity, 0);
    const shippingCost = selectedShipMethod.optionName 
        ? (selectedShipMethod.basePrice + (selectedShipMethod.increment * (numOfItems - 1)))
        : 0;

    const [ getProducts ] = useGetProductsMutation();
    const [ getPrices ] = useGetPricesMutation();
    const [ createCheckoutSession, { isLoading: creatingCheckoutSession } ] = useCreateStripeCheckoutSessionMutation();
    const [ getOrderInfo, { isLoading: orderInfoLoading } ] = useGetOrderInfoMutation();

    const fetchOrderInfo = useCallback( async () => {
        if (orderInfoFetchedRef.current) {
            return;
        }
        orderInfoFetchedRef.current = true;

        const [orderResponse, productResponse, priceResponse] = await Promise.all([
            getOrderInfo(session_id).unwrap(),
            getProducts().unwrap(),
            getPrices().unwrap()
        ])

        let newCategories = ["All"];
        let newCollections = ["All"];
        const productList = productResponse.data.map(product => {
            if (!newCategories.includes(product.metadata.Category) && product.metadata.Category) {
                newCategories.push(product.metadata.Category);
            }
            if (!newCollections.includes(product.metadata.Collection) && product.metadata.Collection) {
                newCollections.push(product.metadata.Collection);
            }
            const newProduct = {
                id: product.id,
                productName: product.name,
                category: product.metadata.Category,
                subcategory: product.metadata.Subcategory,
                collection: product.metadata.Collection,
                productSKU: product.metadata['SKU'],
                imgSrc: product.images[0],
                created: product.created
            }
            const price = priceResponse.data.find(price => product.id === price.product);
            newProduct.price = price.unit_amount;
            newProduct.priceId = price.id;
            return newProduct;
        })
        
        dispatch(setProducts(productList))
        dispatch(setCategories(newCategories));
        dispatch(setCollections(newCollections));
        dispatch(setContactEmail(orderResponse.contactInformation.email));
        dispatch(setContactPhone(orderResponse.contactInformation.phone));
        dispatch(setRecipientFirstName(orderResponse.recipientInformation.firstName));
        dispatch(setRecipientLastName(orderResponse.recipientInformation.lastName));
        dispatch(setShippingAddressLine1(orderResponse.shippingInformation.addressLine1));
        dispatch(setShippingAddressLine2(orderResponse.shippingInformation.addressLine2));
        dispatch(setShippingCity(orderResponse.shippingInformation.city));
        dispatch(setShippingState(orderResponse.shippingInformation.state));
        dispatch(setShippingZip(orderResponse.shippingInformation.zip));

        const cartItems = orderResponse.cart.map(item => {
            const productToAdd = productList.find(product => {
                console.log("Product SKU: " + product.productSKU + " Cart SKU: " + item['SKU']);
                return item['SKU'] === product.productSKU
            });
            
            const newCartItem = {
                ...productToAdd,
                quantity: item.quantity
            }
            return newCartItem;
        });

        cartItems.forEach(item => {
            dispatch(addCartItem(item));
        })

        const shipMethod = shippingOptions.find(option => option.optionName === orderResponse.shipMethod);
        dispatch(setShippingMethod(shipMethod));

    }, [getOrderInfo, session_id, getProducts, getPrices, dispatch, shippingOptions]);

    useEffect(() => {
        if (session_id && !cart.length) {
            fetchOrderInfo();
        }
    }, [session_id, cart, fetchOrderInfo])

    const handleContactEmailChange = (event) => {
        dispatch(setContactEmail(event.target.value));
    }

    const handleContactPhoneChange = (event) => {
        dispatch(setContactPhone(event.target.value));
    }

    const handleFirstNameChange = event => {
        dispatch(setRecipientFirstName(event.target.value));
    }

    const handleLastNameChange = event => {
        dispatch(setRecipientLastName(event.target.value));
    }

    const handleCompanyChange = event => {
        dispatch(setRecipientCompany(event.target.value));
    }

    const handleStreetAddressChange = event => {
        dispatch(setShippingAddressLine1(event.target.value));
    }

    const handleAptSuiteChange = event => {
        dispatch(setShippingAddressLine2(event.target.value));
    }

    const handleCityChange = event => {
        dispatch(setShippingCity(event.target.value));
    }

    const handleStateChange = event => {
        dispatch(setShippingState(event.target.value));
    }

    const handleZipChange = event => {
        dispatch(setShippingZip(event.target.value));
    }

    const handleShipMethodClick = event => {
        const newMethodName = event.target.id;
        const newMethod = shippingOptions.find(option => option.optionName === newMethodName);
        dispatch(setShippingMethod(newMethod));
    }

    const handleProceedToPaymentClick = async event => {
        if (!cart.length || !contactInformation.email || !recipientInformation.firstName || !shippingInformation.addressLine1 || !shippingInformation.city || !shippingInformation.state || !shippingInformation.zip) {
            alert("Please make sure all shipping information is specified before proceeding to payment.");
            return;
        }
        const payload = {
            cart,
            contactInformation,
            recipientInformation,
            shippingInformation,
            shippingMethod: selectedShipMethod.optionName,
            shippingCost
        }

        const session = await createCheckoutSession(payload).unwrap();

        window.location.href = session.url;
    }

    return (
        <section className="shippingPage">
            {(orderInfoLoading) ? <LoadingSpinner /> : ''}
            <div className="breadcrumbs">
                <span>Cart</span>
                <span>⫸</span>
                <span>Shipping</span>
                <span>⫸</span>
                <span>Payment</span>
            </div>
            <div className='pageContainer'>
                <div className='shippingForm'>
                    <h2>Shipping</h2>
                    <p>Tell us where your adventure begins.</p>
                    <section className='contactInfo'>
                        <h3>Contact Information</h3>
                        <input 
                            type='email' 
                            value={contactInformation.email}
                            onChange={handleContactEmailChange}
                            placeholder='Email Address' 
                        />
                        <input 
                            type='phone' 
                            value={contactInformation.phone}
                            onChange={handleContactPhoneChange}
                            placeholder='Phone Number (optional)'
                        />
                    </section>
                    <section className='recipientInfo'>
                        <h3>Recipient Information</h3>
                        <input
                            value={recipientInformation.firstName}
                            onChange={handleFirstNameChange} 
                            placeholder='First Name' 
                        />
                        <input
                            value={recipientInformation.lastName}
                            onChange={handleLastNameChange} 
                            placeholder='Last Name' 
                        />
                        <input
                            value={recipientInformation.company}
                            onChange={handleCompanyChange} 
                            placeholder='Company (optional)' 
                        />
                    </section>
                    <section className='shippingInfo'>
                        <div className='shippingInfoHeader'>
                            <h3>Shipping Information</h3>
                        </div>
                        <input
                            value={shippingInformation.addressLine1}
                            onChange={handleStreetAddressChange} 
                            placeholder='Street Address' 
                        />
                        <input
                            value={shippingInformation.addressLine2}
                            onChange={handleAptSuiteChange} 
                            placeholder='Apt/Suite' 
                        />
                        <div className='cityStateZip'>
                            <input
                                value={shippingInformation.city}
                                onChange={handleCityChange} 
                                placeholder='City' 
                            />
                            <input
                                value={shippingInformation.state}
                                onChange={handleStateChange} 
                                placeholder='State' 
                            />
                            <input 
                                type='text'
                                inputMode='numeric'
                                pattern='[0-9]{5}'
                                maxLength="5"
                                value={shippingInformation.zip}
                                onChange={handleZipChange}
                                placeholder='Zip Code' 
                            />
                        </div>
                    </section>
                </div>
                <div className='orderSummary'>
                    <h2>Order Summary</h2>
                    <h3>Selected gear:</h3>
                    {cart.map(item => {
                        return (
                            <div>
                                <p>x{item.quantity} {item.productName}</p>
                                <p>${((item.price / 100) * item.quantity).toFixed(2)}</p>
                            </div>
                        )
                    })}

                    <div className='subtotal'>
                        <p>Subtotal</p>
                        <p>${(cartTotal / 100).toFixed(2)}</p>
                    </div>
                    <h4>Please allow two weeks for shipping.</h4>
                    <h3>Select Shipping Method</h3>
                    
                    <div className='shippingOptions'>
                        {shippingOptions.map(option => {
                            return (
                                <div 
                                    className={selectedShipMethod.optionName === option.optionName ? 'shippingOptionCard active' : 'shippingOptionCard'} 
                                    id={option.optionName}
                                    onClick={handleShipMethodClick}
                                >
                                    <p id={option.optionName}>{option.optionName}</p>
                                    <p id={option.optionName}>${(option.basePrice + (option.increment * (numOfItems - 1))).toFixed(2)}</p>
                                </div>
                            )
                        })}
                    </div>
                    <div>
                        <p>Shipping</p>
                        <p>${shippingCost.toFixed(2)}</p>
                    </div>
                    <div>
                        <p>Total</p>
                        <p>${((cartTotal / 100) + shippingCost).toFixed(2)}</p>
                    </div>
                    <div className='buttonContainer'>
                        <button onClick={handleProceedToPaymentClick}>Proceed to Payment</button>
                    </div>
                    <div className='reassuranceCards'>
                        <div className='reassuranceCard'>
                            <p>🛡</p>
                            <p>Secure Checkout</p>
                        </div>
                        <div className='reassuranceCard'>
                            <p>📦</p>
                            <p>Carefully Packed</p>
                        </div>
                        <div className='reassuranceCard'>
                            <p>⚒</p>
                            <p>Made to Order</p>
                        </div>
                        <div className='reassuranceCard'>
                            <p>⭐</p>
                            <p>Small Business</p>
                        </div>
                    </div>
                </div>
            </div>
            {creatingCheckoutSession ? <LoadingSpinner /> : ''}  
        </section>
    )
}